package com.devsusana.portfolio.components.blog

import androidx.compose.runtime.*
import com.devsusana.portfolio.components.layouts.PageLayout
import com.devsusana.portfolio.components.widgets.TechBadge
import com.devsusana.portfolio.styles.SitePalette
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

/**
 * High-level layout container for technical articles built purely with Composables.
 * No external CSS stylesheet is required.
 */
@Composable
fun ArticleScaffold(
    title: String,
    date: String,
    readingTime: String,
    tags: List<String>,
    isSpanish: Boolean = false,
    content: @Composable () -> Unit
) {
    val colorMode = ColorMode.current

    LaunchedEffect(isSpanish) {
        if (isSpanish && com.devsusana.portfolio.i18n.LanguageState.currentLanguage != com.devsusana.portfolio.i18n.AppLanguage.ES) {
            com.devsusana.portfolio.i18n.LanguageState.setLanguage(com.devsusana.portfolio.i18n.AppLanguage.ES)
        } else if (!isSpanish && com.devsusana.portfolio.i18n.LanguageState.currentLanguage != com.devsusana.portfolio.i18n.AppLanguage.EN) {
            com.devsusana.portfolio.i18n.LanguageState.setLanguage(com.devsusana.portfolio.i18n.AppLanguage.EN)
        }
    }

    PageLayout(title = "$title | Susana Córdoba") {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(960.px)
                .padding(top = 0.cssRem, bottom = 3.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(0.75.cssRem)
        ) {
            // Minimalist back navigation arrow
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 4.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Link(
                    path = "/#blog",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = Modifier.cursor(Cursor.Pointer)
                ) {
                    Row(
                        modifier = Modifier
                            .size(34.px)
                            .borderRadius(8.px)
                            .backgroundColor(
                                if (colorMode.isDark) Color.rgba(255, 255, 255, 0.06f)
                                else Color.rgba(0, 0, 0, 0.05f)
                            )
                            .border(1.px, LineStyle.Solid, SitePalette.surfaceBorder(colorMode)),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Center
                    ) {
                        SpanText(
                            text = "←",
                            modifier = Modifier
                                .fontSize(1.2.cssRem)
                                .fontWeight(FontWeight.Bold)
                                .color(if (colorMode.isDark) SitePalette.androidGreen else Color.rgb(0x04, 0x78, 0x57))
                        )
                    }
                }
            }

            // Article Body Container Card
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .backgroundColor(SitePalette.surface(colorMode))
                    .borderRadius(16.px)
                    .border(1.px, LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
                    .styleModifier {
                        property("padding", "clamp(1rem, 3vw, 2.25rem)")
                        property(
                            "box-shadow",
                            if (colorMode.isDark) "0 10px 30px -10px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)"
                            else "0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -2px rgba(0, 0, 0, 0.03)"
                        )
                    }
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(1.25.cssRem)
                ) {
                    // Header Metadata
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            text = "$date • $readingTime",
                            modifier = Modifier
                                .color(SitePalette.textSecondary(colorMode))
                                .fontSize(0.9.cssRem)
                                .fontWeight(FontWeight.Medium)
                        )
                    }

                    // H1 Title with responsive fluid typography
                    H1 {
                        SpanText(
                            text = title,
                            modifier = Modifier
                                .color(SitePalette.textPrimary(colorMode))
                                .styleModifier { property("font-size", "clamp(1.35rem, 4.5vw, 2.15rem)") }
                                .fontWeight(FontWeight.Bold)
                                .lineHeight(1.25)
                        )
                    }

                    // Tags
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .styleModifier {
                                property("flex-wrap", "wrap")
                                property("gap", "0.5rem")
                            },
                        horizontalArrangement = Arrangement.Start
                    ) {
                        tags.forEach { tag ->
                            TechBadge(tag)
                        }
                    }

                    HorizontalDivider(modifier = Modifier.fillMaxWidth().margin(topBottom = 0.5.cssRem))

                    // Article Content Slot
                    content()
                }
            }
        }
    }
}

/**
 * Section title (H2) with clean spacing and subtle bottom border accent.
 */
@Composable
fun ArticleSectionHeader(title: String) {
    val colorMode = ColorMode.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 1.75.cssRem, bottom = 0.5.cssRem),
        verticalArrangement = Arrangement.spacedBy(0.5.cssRem)
    ) {
        H2 {
            SpanText(
                text = title,
                modifier = Modifier
                    .color(SitePalette.textPrimary(colorMode))
                    .styleModifier { property("font-size", "clamp(1.2rem, 3.5vw, 1.45rem)") }
                    .fontWeight(FontWeight.Bold)
                    .lineHeight(1.35)
            )
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(1.px)
                .backgroundColor(SitePalette.surfaceBorder(colorMode))
        )
    }
}

/**
 * Sub-section title (H3) with cyan/accent highlighting.
 */
@Composable
fun ArticleSubHeader(title: String) {
    val colorMode = ColorMode.current
    H3 {
        SpanText(
            text = title,
            modifier = Modifier
                .color(if (colorMode.isDark) Color.rgb(0x38, 0xBD, 0xF8) else Color.rgb(0x02, 0x84, 0xC7))
                .fontSize(1.2.cssRem)
                .fontWeight(FontWeight.Bold)
                .padding(top = 1.cssRem, bottom = 0.25.cssRem)
        )
    }
}

/**
 * Clean body text paragraph with ergonomic typography and line height.
 */
@Composable
fun ArticleParagraph(text: String) {
    val colorMode = ColorMode.current
    P {
        SpanText(
            text = text,
            modifier = Modifier
                .color(SitePalette.textSecondary(colorMode))
                .fontSize(1.05.cssRem)
                .lineHeight(1.8.cssRem)
        )
    }
}

/**
 * Rich code snippet with interactive copy-to-clipboard, language label,
 * soft wrapping to prevent clipping, and syntax terminal styling.
 */
@Composable
fun CodeSnippet(code: String, language: String = "Kotlin") {
    val colorMode = ColorMode.current
    var copied by remember { mutableStateOf(false) }

    LaunchedEffect(copied) {
        if (copied) {
            delay(2000)
            copied = false
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(12.px)
            .backgroundColor(Color.rgb(0x09, 0x0D, 0x16))
            .border(1.px, LineStyle.Solid, if (colorMode.isDark) Color.rgb(0x1E, 0x29, 0x3B) else Color.rgb(0x33, 0x41, 0x55))
            .margin(topBottom = 1.25.cssRem)
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            // Code header bar
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .backgroundColor(Color.rgba(255, 255, 255, 0.04f))
                    .padding(leftRight = 1.cssRem, topBottom = 0.5.cssRem)
                    .borderBottom(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f)),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    text = language.uppercase(),
                    modifier = Modifier
                        .color(SitePalette.androidGreen)
                        .fontSize(0.75.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .fontFamily("ui-monospace", "JetBrains Mono", "monospace")
                )

                // Interactive copy button
                Box(
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .borderRadius(6.px)
                        .padding(leftRight = 8.px, topBottom = 4.px)
                        .backgroundColor(
                            if (copied) Color.rgba(61, 220, 132, 0.2f)
                            else Color.rgba(255, 255, 255, 0.06f)
                        )
                        .onClick {
                            try {
                                kotlinx.browser.window.navigator.clipboard.writeText(code)
                                copied = true
                            } catch (_: Throwable) {}
                        }
                ) {
                    SpanText(
                        text = if (copied) "✓ Copied" else "Copy",
                        modifier = Modifier
                            .color(if (copied) SitePalette.androidGreen else Color.rgb(0x94, 0xA3, 0xB8))
                            .fontSize(0.75.cssRem)
                            .fontWeight(FontWeight.Medium)
                    )
                }
            }

            // Code Content Body
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(1.25.cssRem)
                    .styleModifier {
                        property("overflow-x", "auto")
                    }
            ) {
                Pre(
                    attrs = {
                        style {
                            property("margin", "0")
                            property("padding", "0")
                            property("box-sizing", "border-box")
                            property("width", "100%")
                            property("white-space", "pre-wrap")
                            property("word-break", "break-word")
                            property("overflow-wrap", "anywhere")
                            property("font-family", "ui-monospace, 'JetBrains Mono', 'Fira Code', monospace")
                            property("font-size", "0.9rem")
                            property("line-height", "1.65")
                            property("color", "#E2E8F0")
                        }
                    }
                ) {
                    Code {
                        Text(code)
                    }
                }
            }
        }
    }
}

/**
 * Formatted bullet point item with subtle green accent dot.
 */
@Composable
fun BulletItem(boldLabel: String, text: String) {
    val colorMode = ColorMode.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 0.3.cssRem, bottom = 0.3.cssRem, left = 0.5.cssRem),
        horizontalArrangement = Arrangement.spacedBy(0.6.cssRem),
        verticalAlignment = Alignment.Top
    ) {
        SpanText(
            text = "•",
            modifier = Modifier
                .color(SitePalette.androidGreen)
                .fontWeight(FontWeight.Bold)
                .fontSize(1.1.cssRem)
        )
        P(attrs = { style { property("margin", "0") } }) {
            SpanText(
                text = "$boldLabel ",
                modifier = Modifier
                    .color(SitePalette.textPrimary(colorMode))
                    .fontWeight(FontWeight.Bold)
                    .fontSize(1.02.cssRem)
            )
            SpanText(
                text = text,
                modifier = Modifier
                    .color(SitePalette.textSecondary(colorMode))
                    .fontSize(1.02.cssRem)
                    .lineHeight(1.65.cssRem)
            )
        }
    }
}

/**
 * Callout box for takeaways, important notes, and conclusions.
 */
@Composable
fun CalloutBox(content: @Composable () -> Unit) {
    val colorMode = ColorMode.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .margin(topBottom = 1.25.cssRem)
            .padding(1.25.cssRem)
            .borderLeft(4.px, LineStyle.Solid, SitePalette.androidGreen)
            .borderRadius(topRight = 10.px, bottomRight = 10.px)
            .backgroundColor(
                if (colorMode.isDark) Color.rgba(61, 220, 132, 0.08f)
                else Color.rgba(5, 150, 105, 0.06f)
            )
    ) {
        content()
    }
}

/**
 * Structured, responsive table for architectural comparisons.
 */
@Composable
fun ComparisonTable(
    headers: List<String>,
    rows: List<List<String>>
) {
    val colorMode = ColorMode.current

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .margin(topBottom = 1.5.cssRem)
            .borderRadius(10.px)
            .border(1.px, LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
            .styleModifier {
                property("overflow-x", "auto")
            }
    ) {
        Table(
            attrs = {
                style {
                    property("width", "100%")
                    property("border-collapse", "collapse")
                    property("font-size", "0.95rem")
                    property("line-height", "1.6")
                    property("background-color", if (colorMode.isDark) "#131B2E" else "#F8FAFC")
                }
            }
        ) {
            Thead {
                Tr {
                    headers.forEach { header ->
                        Th(
                            attrs = {
                                style {
                                    property("padding", "14px 18px")
                                    property("text-align", "left")
                                    property("font-weight", "bold")
                                    property("font-size", "0.85rem")
                                    property("text-transform", "uppercase")
                                    property("letter-spacing", "0.05em")
                                    property("color", if (colorMode.isDark) "#3DDC84" else "#047857")
                                    property("background-color", if (colorMode.isDark) "rgba(61, 220, 132, 0.12)" else "rgba(5, 150, 105, 0.08)")
                                    property("border-bottom", if (colorMode.isDark) "2px solid #334155" else "2px solid #CBD5E1")
                                }
                            }
                        ) {
                            Text(header)
                        }
                    }
                }
            }
            Tbody {
                rows.forEachIndexed { index, row ->
                    Tr(
                        attrs = {
                            style {
                                property("border-bottom", if (index < rows.size - 1) (if (colorMode.isDark) "1px solid #1E293B" else "1px solid #E2E8F0") else "none")
                            }
                        }
                    ) {
                        row.forEachIndexed { colIndex, cell ->
                            Td(
                                attrs = {
                                    style {
                                        property("padding", "14px 18px")
                                        property("vertical-align", "top")
                                        property("color", if (colIndex == 0) (if (colorMode.isDark) "#F8FAFC" else "#0F172A") else (if (colorMode.isDark) "#CBD5E1" else "#334155"))
                                        if (colIndex == 0) property("font-weight", "600")
                                    }
                                }
                            ) {
                                Text(cell)
                            }
                        }
                    }
                }
            }
        }
    }
}
